// File: app/src/utils/performanceProfile.js
//
// Pure bridge between the unified "Performance Profile" CRUD list (the
// performance_capabilities rows) and the rep-time estimator. The list is the
// single source of truth for both the LLM prompt (loose, free text) and the
// timer (strict, keyed lookup); this module translates the rows back into the
// `training_data`-shaped object the estimator already consumes.
//
// No React, no I/O, no module-level state.

import { normaliseExerciseKey } from "./repEstimator";
import { combineOneRm, isOneRmMetric } from "./benchmarkNormalise";

// The four lifts the VBT physics model covers. A row only feeds VBT when it is
// in kilograms, its label normalises to one of these keys, AND it is either
// marked as a 1RM or gives the reps it was lifted for, so an estimated 1RM can
// be worked out (issue #88). A bare working weight never counts as a max.
export const VBT_LIFT_KEYS = ["squat", "bench", "deadlift", "ohp"];

// Canonical labels used when backfilling/seeding 1RM rows from legacy oneRms.
export const VBT_LIFT_LABELS = {
  squat: "Back Squat",
  bench: "Bench Press",
  deadlift: "Deadlift",
  ohp: "Overhead Press",
};

const VBT_LIFT_SET = new Set(VBT_LIFT_KEYS);

const toPositiveNumber = (raw) => {
  // pg returns NUMERIC as strings; coerce before validating.
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export { isOneRmMetric };

const isVbtLiftRow = (cap) =>
  cap && cap.unit === "kg" && VBT_LIFT_SET.has(normaliseExerciseKey(cap.label));

/**
 * Build the estimator's `trainingData` object from the unified capabilities
 * list. Strength rows on a VBT lift populate `oneRms` with one 1RM per lift,
 * combined from every row by recency and firmness (combineOneRm, issue #91),
 * the same figure the AI is given. Rows with a `sec_per_rep` tempo populate
 * `tempos` (a per-rep override for ANY exercise).
 *
 * @param {Array}  capabilities          performance_capabilities rows.
 * @param {Object} opts
 * @param {string} opts.pace             "fast" | "standard" | "controlled".
 * @param {Object} opts.fallbackTrainingData  Legacy profile.training_data; its
 *        oneRms/workingWeights fill keys not present in the list (transition
 *        fallback while the 1RM backfill rolls out).
 * @param {Date}   opts.now              Today, for weighting by age.
 * @returns {{ pace: string, oneRms: Object, tempos: Object, workingWeights: Object }}
 */
export const deriveTrainingData = (capabilities, opts = {}) => {
  const { pace, fallbackTrainingData, now = new Date() } = opts;
  const oneRms = {};
  const tempos = {};
  const liftRows = {}; // key → the rows on that lift

  for (const cap of capabilities || []) {
    if (isVbtLiftRow(cap)) {
      const key = normaliseExerciseKey(cap.label);
      liftRows[key] = [...(liftRows[key] || []), cap];
    }

    const tempo = toPositiveNumber(cap.sec_per_rep);
    if (tempo != null) {
      tempos[normaliseExerciseKey(cap.label)] = tempo;
    }
  }

  for (const [key, rows] of Object.entries(liftRows)) {
    const combined = combineOneRm(rows, now);
    if (combined) oneRms[key] = combined.kg;
  }

  // Fill any VBT lift not yet represented in the list from legacy training_data
  // so already-configured users keep VBT until the backfill lands.
  const fallbackOneRms = fallbackTrainingData?.oneRms;
  if (fallbackOneRms) {
    for (const key of VBT_LIFT_KEYS) {
      if (oneRms[key] == null) {
        const oneRm = toPositiveNumber(fallbackOneRms[key]);
        if (oneRm != null) oneRms[key] = oneRm;
      }
    }
  }

  return {
    pace: pace || fallbackTrainingData?.pace || "standard",
    oneRms,
    tempos,
    workingWeights: fallbackTrainingData?.workingWeights || {},
  };
};
